import { Save, Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { 
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
    Lock,
    ShieldAlert
} from 'lucide-react';

interface AssistantActionsProps {
    onSave: () => void;
    assistantId?: string;
    isLoading?: boolean;
    isDuplicating?: boolean;
    onDuplicate?: () => void;
}

export function AssistantActions({ onSave, assistantId, isLoading, isDuplicating, onDuplicate }: AssistantActionsProps) {
    const { user } = useAuth();
    const canEditAssistant = user?.permissions?.assistant === 'EDITOR';

    return (
        <div className="flex justify-end space-x-2">
            {assistantId && (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={onDuplicate}
                                    disabled={isDuplicating || !canEditAssistant}
                                    className="gap-2"
                                >
                                    {isDuplicating ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Duplicating
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="h-4 w-4" />
                                            Duplicate
                                        </>
                                    )}
                                    {!canEditAssistant && <Lock className="w-3.5 h-3.5 ml-2 opacity-70" />}
                                </Button>
                            </div>
                        </TooltipTrigger>
                        {!canEditAssistant && (
                            <TooltipContent className="flex items-center gap-2 max-w-xs">
                                <ShieldAlert className="w-12 h-12 text-amber-500" />
                                <p>You need editor permissions to duplicate assistants.</p>
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            )}
            
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div>
                            <Button
                                variant="default"
                                size="sm"
                                onClick={onSave}
                                disabled={isLoading || !canEditAssistant}
                                className="gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Saving
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        {assistantId ? 'Save Changes' : 'Create Assistant'}
                                        {!canEditAssistant && <Lock className="w-3.5 h-3.5 ml-2 opacity-70" />}
                                    </>
                                )}
                            </Button>
                        </div>
                    </TooltipTrigger>
                    {!canEditAssistant && (
                        <TooltipContent className="flex items-center gap-2 max-w-xs">
                            <ShieldAlert className="w-12 h-12 text-amber-500" />
                            <p>You need editor permissions to {assistantId ? 'save changes to' : 'create'} assistants. Contact your administrator for access.</p>
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}